import { Crop, ExternalLink, ImagePlus, RefreshCw, Trash2, UploadCloud, X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react'
import { toApiError } from '@/api/errors'
import { useT, type Translate } from '@/i18n'
import { cn } from '@/lib/cn'
import { filesService } from '@/services'
import { useStores } from '@/stores'
import { photoUrl } from './Avatar'
import { Button } from './Button'
import { ImageCropDialog, SQUARE_CROP, type ImageCrop } from './ImageCropDialog'

/** Formats the console accepts for photos (the backend sniffs the real type as well). */
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp']
const DEFAULT_MAX_MB = 10

/**
 * While a file is on its way, a form field holds this marker instead of a URL,
 * so form validation can refuse to save before the photo has arrived.
 */
export const UPLOADING_VALUE = 'uploading:'
export const isUploading = (value: unknown) => typeof value === 'string' && value.startsWith(UPLOADING_VALUE)

type ImageUploadProps = {
  id?: string
  value: string
  onChange: (url: string) => void
  disabled?: boolean
  /** Field error from the form (required, still uploading…): shown as an invalid state. */
  invalid?: boolean
  /** Put UPLOADING_VALUE into the value while a file uploads (for the form engine's validation). */
  reportPending?: boolean
  maxSizeMb?: number
  /**
   * How the photo is cut before it uploads, and the shape it shows in:
   * AVATAR_CROP (circle) for a person; by default SQUARE_CROP, a 1:1 square.
   */
  crop?: ImageCrop
}

/** Rejects a file before it is sent: wrong type, too large, or not a decodable image. */
async function fileProblem(file: File, maxSizeMb: number, t: Translate): Promise<string | null> {
  if (!ACCEPTED.includes(file.type)) return t('workbench.image.badType')
  if (file.size > maxSizeMb * 1024 * 1024) return t('workbench.image.tooLarge', { size: maxSizeMb })
  if (file.size === 0) return t('workbench.image.broken')
  try {
    // A renamed non-image fails here even with an image extension and type.
    const bitmap = await createImageBitmap(file)
    bitmap.close()
  } catch {
    return t('workbench.image.broken')
  }
  return null
}

/**
 * Photo field: click (or Enter/Space), drag and drop, or paste an image. The
 * file is checked first (PNG, JPG or WebP, size limit, really an image), shows
 * at once as a preview, uploads with a progress bar and can be cancelled. A set
 * photo can be opened, replaced or removed.
 */
export function ImageUpload({ id, value, onChange, disabled, invalid, reportPending, maxSizeMb = DEFAULT_MAX_MB, crop = SQUARE_CROP }: ImageUploadProps) {
  const { session } = useStores()
  const t = useT()
  const hintId = useId()
  const input = useRef<HTMLInputElement>(null)
  const upload = useRef<{ controller: AbortController; previous: string } | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  // A checked file waiting in the crop dialog.
  const [cropping, setCropping] = useState<File | null>(null)
  const [fetching, setFetching] = useState(false)
  const canUpload = session.can('files.upload')
  const inactive = disabled || !canUpload
  const busy = progress !== null
  const current = isUploading(value) ? '' : photoUrl(value) ?? ''
  const shown = preview ?? current

  // Free the local preview when it is replaced or the field goes away.
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview)
  }, [preview])
  useEffect(() => () => upload.current?.controller.abort(), [])

  const take = async (file?: File | null) => {
    if (!file || inactive || busy || cropping) return
    setError(null)
    const problem = await fileProblem(file, maxSizeMb, t)
    if (problem) {
      setError(problem)
      return
    }
    setCropping(file)
  }

  const send = async (file: File) => {
    const controller = new AbortController()
    const previous = current
    upload.current = { controller, previous }
    setPreview(URL.createObjectURL(file))
    setProgress(0)
    if (reportPending) onChange(`${UPLOADING_VALUE}${file.name}`)
    try {
      const uploaded = await filesService.upload(file, setProgress, controller.signal)
      onChange(uploaded.url)
    } catch (cause) {
      const apiError = toApiError(cause)
      if (!apiError.isCancelled) setError(apiError.message || t('workbench.image.failed'))
      onChange(previous)
    } finally {
      upload.current = null
      setProgress(null)
      setPreview(null)
      if (input.current) input.current.value = ''
    }
  }

  /** Opens the photo already set in the crop dialog, to zoom and move it again. */
  const editCurrent = async () => {
    if (!current || inactive || busy || fetching) return
    setError(null)
    setFetching(true)
    try {
      const response = await fetch(current)
      if (!response.ok) throw new Error(String(response.status))
      const blob = await response.blob()
      const type = ACCEPTED.includes(blob.type) ? blob.type : 'image/png'
      const name = decodeURIComponent(current.split('/').pop()?.split('?')[0] || 'photo')
      setCropping(new File([blob], name, { type }))
    } catch {
      // Usually the file host refusing a cross-origin read: a new upload still works.
      setError(t('workbench.image.editFailed'))
    } finally {
      setFetching(false)
    }
  }

  const open = () => {
    if (!inactive && !busy && !cropping) input.current?.click()
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    void take(event.dataTransfer.files?.[0])
  }

  const onPaste = (event: ClipboardEvent) => {
    const file = [...event.clipboardData.files].find((item) => item.type.startsWith('image/'))
    if (!file) return
    event.preventDefault()
    void take(file)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      open()
    }
  }

  const round = crop.shape === 'circle'

  const hint = !canUpload
    ? t('workbench.uploadForbidden')
    : error
      ? error
      : busy
        ? t('workbench.uploading', { percent: progress ?? 0 })
        : t('workbench.image.hint', { size: maxSizeMb })

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-stretch gap-3">
        <div
          id={id}
          role="button"
          tabIndex={inactive || busy ? -1 : 0}
          aria-disabled={inactive || busy || undefined}
          aria-invalid={invalid || Boolean(error) || undefined}
          aria-describedby={hintId}
          aria-label={shown ? t('workbench.replaceImage') : t('workbench.uploadImage')}
          onClick={open}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onDragOver={(event) => {
            event.preventDefault()
            if (!inactive && !busy) setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          style={{ aspectRatio: round ? 1 : (crop.aspect ?? 1) }}
          className={cn(
            'group relative flex h-24 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden border-2 border-dashed text-center outline-none transition-[border-color,background-color,box-shadow] duration-[var(--dur-base)]',
            'focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]',
            round ? 'rounded-full' : 'rounded-[calc(14px*var(--radius-scale))]',
            dragging ? 'border-accent bg-accent-soft' : invalid || error ? 'border-danger bg-danger-soft/40' : shown ? 'border-transparent bg-hover' : 'border-line-strong bg-hover hover:border-accent hover:bg-accent-soft/40',
            (inactive || busy) && 'cursor-not-allowed',
            inactive && 'opacity-60',
            // Locked: gray like a disabled field, no hover invite.
            inactive && !shown && 'bg-field-disabled hover:border-line-strong hover:bg-field-disabled',
          )}
        >
          {shown ? (
            <>
              <img src={shown} alt="" className={cn('absolute inset-0 size-full', round ? 'object-cover' : 'object-contain', busy && 'opacity-60')} />
              {!busy && !inactive && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/35 text-white opacity-0 transition-opacity duration-[var(--dur-base)] group-hover:opacity-100 group-focus-visible:opacity-100">
                  <RefreshCw className="size-5" />
                </span>
              )}
            </>
          ) : dragging ? (
            <UploadCloud className="size-6 text-accent" />
          ) : (
            <>
              <ImagePlus className="size-5 text-subtle" strokeWidth={1.75} />
              <span className="px-1 text-[length:calc(11px*var(--text-scale))] leading-tight text-muted">{t('workbench.image.drop')}</span>
            </>
          )}
          {busy && (
            <span className="absolute inset-x-0 bottom-0 h-1.5 bg-press">
              <span className="block h-full bg-accent transition-[width] duration-150" style={{ width: `${progress}%` }} />
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col justify-center gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            {busy ? (
              <Button size="sm" variant="ghost" icon={<X className="size-3.5" />} onClick={() => upload.current?.controller.abort()}>
                {t('common.actions.cancel')}
              </Button>
            ) : (
              <>
                <Button size="sm" disabled={inactive} onClick={open}>
                  {current ? t('workbench.replaceImage') : t('workbench.uploadImage')}
                </Button>
                {current && (
                  <>
                    <Button size="sm" variant="ghost" loading={fetching} disabled={inactive} icon={<Crop className="size-3.5" />} onClick={() => void editCurrent()}>
                      {t('workbench.image.edit')}
                    </Button>
                    <Button size="sm" variant="ghost" icon={<ExternalLink className="size-3.5" />} onClick={() => window.open(current, '_blank', 'noopener,noreferrer')}>
                      {t('workbench.image.open')}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={disabled} icon={<Trash2 className="size-3.5" />} onClick={() => onChange('')}>
                      {t('common.actions.remove')}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
          <p id={hintId} role={error ? 'alert' : undefined} className={cn('text-[length:calc(12px*var(--text-scale))]', error ? 'text-danger' : 'text-muted')}>
            {hint}
          </p>
        </div>
      </div>
      {cropping && (
        <ImageCropDialog
          file={cropping}
          crop={crop}
          onDone={(cropped) => {
            setCropping(null)
            if (input.current) input.current.value = ''
            if (cropped) void send(cropped)
          }}
        />
      )}
      <input ref={input} type="file" accept={ACCEPTED.join(',')} hidden onChange={(event) => void take(event.target.files?.[0])} />
    </div>
  )
}
